import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Download } from "lucide-react";

interface ExportRecord {
  id: string;
  created_at: string;
  format: string;
  start_date: string;
  end_date: string;
  record_count: number;
  file_name: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message: string | null;
}

export const ExportHistory = () => {
  const [exports, setExports] = useState<ExportRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchExportHistory();
  }, []);

  const fetchExportHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('exports')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      setExports((data || []).map(d => ({
        ...d,
        status: d.status as 'pending' | 'processing' | 'completed' | 'failed'
      })));
    } catch (error) {
      console.error('Failed to fetch export history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return "px-2 py-1 text-xs bg-green-100 text-green-800 rounded-md font-medium";
      case 'failed':
        return "px-2.5 py-1 text-xs bg-red-100 text-red-800 rounded-2xl font-medium";
      case 'processing':
        return "px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-md font-medium";
      case 'pending':
        return "px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-md font-medium";
      default:
        return "px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-md font-medium";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'processing':
        return 'Processing';
      case 'pending':
        return 'Pending';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-brand-border">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-brand-primary">Export History</h3>
        <button
          onClick={fetchExportHistory}
          className="h-7 px-3 py-1.5 bg-brand-secondary rounded-lg text-xs text-brand-primary font-medium hover:bg-brand-secondary/80"
        >
          <i className="fa-solid fa-rotate-right mr-1"></i>
          Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
        </div>
      ) : exports.length === 0 ? (
        <div className="text-center py-12">
          <i className="fa-solid fa-file-export text-4xl text-brand-text-secondary mb-3"></i>
          <p className="text-brand-text-secondary">No exports yet</p>
          <p className="text-xs text-brand-text-secondary mt-1">
            Create your first export using the wizard above
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-brand-secondary rounded">
                <th className="px-4 py-2 text-left text-[10px] text-brand-text-secondary font-medium tracking-wider">DATE</th>
                <th className="px-4 py-2 text-left text-[10px] text-brand-text-secondary font-medium tracking-wider">PERIOD</th>
                <th className="px-4 py-2 text-left text-[10px] text-brand-text-secondary font-medium tracking-wider">FORMAT</th>
                <th className="px-4 py-2 text-left text-[10px] text-brand-text-secondary font-medium tracking-wider">RECORDS</th>
                <th className="px-4 py-2 text-left text-[10px] text-brand-text-secondary font-medium tracking-wider">STATUS</th>
                <th className="px-4 py-2 text-left text-[10px] text-brand-text-secondary font-medium tracking-wider">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {exports.map((record, index) => (
                <tr key={record.id} className={index < exports.length - 1 ? "border-b border-brand-border" : ""}>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-brand-text-primary">
                    {new Date(record.created_at).toLocaleDateString('de-CH', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}{' '}
                    {new Date(record.created_at).toLocaleTimeString('de-CH', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-brand-text-primary">
                    {new Date(record.start_date).toLocaleDateString('de-CH', { day: '2-digit', month: 'short' })} -{' '}
                    {new Date(record.end_date).toLocaleDateString('de-CH', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-brand-text-primary">
                    {record.format.toUpperCase()}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-brand-text-primary">
                    {record.record_count}
                  </td>
                  <td className="px-4 py-3">
                    <span className={getStatusBadge(record.status)}>
                      {getStatusText(record.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-brand-primary space-x-3">
                    {record.status === 'completed' && record.file_name && (
                      <button
                        title={`Download ${record.file_name}`}
                        className="hover:text-brand-primary/70 transition-colors"
                      >
                        <Download className="h-4 w-4 inline" />
                      </button>
                    )}
                    {record.status === 'failed' && record.error_message && (
                      <button
                        title={record.error_message}
                        className="text-red-600 hover:text-red-700 transition-colors"
                      >
                        <i className="fa-solid fa-exclamation-circle"></i>
                      </button>
                    )}
                    <button
                      title="Repeat export"
                      className="hover:text-brand-primary/70 transition-colors"
                    >
                      <i className="fa-solid fa-rotate-right"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
